// CooldownReductionEvent.tsx

import React, { useContext } from 'react';
import { BaseRendererProps } from './eventRenderers';
import './CooldownReductionEvent.css';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTurnUp } from '@fortawesome/free-solid-svg-icons';
import { getUniversalSourceIcon } from './StatChangeEvent';

/**
 * Simplified cooldown reduction event renderer
 * Shows: [CDR Icon] [Target Icon] [Reduction Numbers]
 *        [Arrow] [Source Icon] (if has specific source)
 */
const CooldownReductionEvent: React.FC<BaseRendererProps> = ({ log }) => {
  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);
  const { abilityHasteIcon } = gameWideAssets;

  const details = log.details;
  const {
    // The champion & ability that is being reduced (target)
    championName,
    cooldownSource,
    cooldownSourceType,
    abilityKey,

    // The cause of the cooldown reduction (source)
    source,
    sourceType,
    hasSpecificSource,

    reductionAmount,
    percentageReduction,
  } = details;

  const imageState = log.imageState;

  // Don't show the event if there's no actual reduction
  if (!reductionAmount || reductionAmount <= 0) {
    return null;
  }

  // Get target icon (what's being reduced) using universal fetcher
  const cooldownSourceIcon = getUniversalSourceIcon(
    {
      sourceType: cooldownSourceType || 'Unknown',
      imageState: imageState,
      sourceName: cooldownSource,
      championName: championName,
      abilityKey: abilityKey,
    },
    gameWideAssets,
    championsAssets
  );

  // Get source icon (what's causing the reduction) using universal fetcher
  // For items, pass the source name as imageState since that's what the universal fetcher uses
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: sourceType || 'Unknown',
      imageState: sourceType === 'Item' ? source : undefined, // Item names are looked up via imageState
      sourceName: source,
      championName: championName,
      abilityKey: source, // Use the source ("q") as the ability key, not the target ability
    },
    gameWideAssets,
    championsAssets
  );

  return (
    <div className="cooldown-reduction-event">
      <div className="cdr-row">
        {/* CDR Icon - Use the actual ability haste icon from assets */}
        <div className="cdr-icon-container">
          <img
            src={abilityHasteIcon}
            alt="Cooldown Reduction"
            className="cdr-icon-image"
          />
        </div>
        
        {/* Target Icon (what's being reduced) */}
        <div className="target-icon-section" style={{ display: 'flex', alignItems: 'center' }}>
          {cooldownSourceIcon}
        </div>
        
        {/* Column of numbers: raw number on top, percentage underneath */}
        <div className="reduction-column">
          <div className="reduction-amount">
            -{reductionAmount.toFixed(1)}s
          </div>
          <div className="reduction-percentage">
            {(percentageReduction).toFixed(1)}%
          </div>
        </div>
      </div>
      
      {/* Source section with arrow (what's causing the reduction) */}
      {sourceIcon && hasSpecificSource && (
        <div className="cdr-source-wrapper">
          <FontAwesomeIcon icon={faTurnUp} className="fa-turn-up" />
          {sourceIcon}
        </div>
      )}
    </div>
  );
};

export default CooldownReductionEvent;
